/**
 * A minimal ZIP writer: stored entries, no compression.
 *
 * Enough to hand over several exported JPEGs as one download. JPEG data is
 * already compressed, so deflating it again would only cost time.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[n] = c >>> 0
  }
  return table
})()

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff
  for (let i = 0; i < data.length; i++) crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

/** DOS date and time, as ZIP headers carry them. */
function dosTime(when: Date): { time: number; date: number } {
  return {
    time: (when.getHours() << 11) | (when.getMinutes() << 5) | Math.floor(when.getSeconds() / 2),
    date: ((when.getFullYear() - 1980) << 9) | ((when.getMonth() + 1) << 5) | when.getDate(),
  }
}

export async function zipFiles(files: { name: string; data: Blob }[]): Promise<Blob> {
  const encoder = new TextEncoder()
  const { time, date } = dosTime(new Date())
  const parts: BlobPart[] = []
  const directory: Uint8Array<ArrayBuffer>[] = []
  let offset = 0

  for (const file of files) {
    const bytes = new Uint8Array(await file.data.arrayBuffer())
    const name = encoder.encode(file.name) as Uint8Array<ArrayBuffer>
    const crc = crc32(bytes)

    const local = new DataView(new ArrayBuffer(30))
    local.setUint32(0, 0x04034b50, true)      // local file header
    local.setUint16(4, 20, true)              // version needed
    local.setUint16(6, 0x0800, true)          // UTF-8 names
    local.setUint16(8, 0, true)               // stored
    local.setUint16(10, time, true)
    local.setUint16(12, date, true)
    local.setUint32(14, crc, true)
    local.setUint32(18, bytes.length, true)
    local.setUint32(22, bytes.length, true)
    local.setUint16(26, name.length, true)
    local.setUint16(28, 0, true)
    parts.push(local.buffer, name, bytes)

    const central = new DataView(new ArrayBuffer(46))
    central.setUint32(0, 0x02014b50, true)    // central directory header
    central.setUint16(4, 20, true)
    central.setUint16(6, 20, true)
    central.setUint16(8, 0x0800, true)
    central.setUint16(10, 0, true)
    central.setUint16(12, time, true)
    central.setUint16(14, date, true)
    central.setUint32(16, crc, true)
    central.setUint32(20, bytes.length, true)
    central.setUint32(24, bytes.length, true)
    central.setUint16(28, name.length, true)
    central.setUint32(42, offset, true)
    const entry = new Uint8Array(46 + name.length)
    entry.set(new Uint8Array(central.buffer), 0)
    entry.set(name, 46)
    directory.push(entry)

    offset += 30 + name.length + bytes.length
  }

  const size = directory.reduce((n, d) => n + d.length, 0)
  const end = new DataView(new ArrayBuffer(22))
  end.setUint32(0, 0x06054b50, true)          // end of central directory
  end.setUint16(8, files.length, true)
  end.setUint16(10, files.length, true)
  end.setUint32(12, size, true)
  end.setUint32(16, offset, true)
  return new Blob([...parts, ...directory, end.buffer], { type: 'application/zip' })
}
